import { FilterTh, describeColumnFilters } from '../common/column-filter-indicator'
/** File-backed catalog; search and paging are applied by the API (ADR-0070). */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDebouncedRosterSearch } from '@speaking-coach/roster-list'
import { queryKeys } from '../api/query-keys'
import { Button, Icon, Notice, NoticeAction, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@speaking-coach/ui'
import { FilterField, ListPage, ListPagination, SearchField } from '../common/list-page'
import { TABLE, TABLE_HEAD_ROW, TABLE_ROW, TABLE_WRAP } from '../common/table'
import { adminI18nClient } from './api'
import type { CatalogParams } from './types'

const PAGE_SIZE = 50

export function TranslationsPage(): JSX.Element {
  const [url, setUrl] = useSearchParams()
  const search = url.get('search') ?? ''
  const debounced = useDebouncedRosterSearch(search)
  const [copyStatus, setCopyStatus] = useState('')
  const [copied, setCopied] = useState<{ key: string } | null>(null)
  useEffect(() => {
    if (!copied) return
    const timeout = window.setTimeout(() => setCopied(null), 3000)
    return () => window.clearTimeout(timeout)
  }, [copied])
  const rawPage = Number(url.get('page') ?? '1')
  const page = Number.isSafeInteger(rawPage) && rawPage >= 1 && rawPage <= 200001 ? rawPage - 1 : 0
  const namespace = url.get('namespace') || undefined
  const version = url.get('version') || undefined

  const params: CatalogParams = { search: debounced || undefined, namespace, locale: 'ja', limit: PAGE_SIZE, offset: page * PAGE_SIZE, catalogVersion: version }
  const catalog = useQuery({
    queryKey: queryKeys.i18nKeys(params),
    queryFn: ({ signal }) => adminI18nClient.list(params, signal),
    staleTime: 0,
    enabled: debounced === search.trim(),
    placeholderData: keepPreviousData,
  })
  const data = catalog.data
  // Pin subsequent pages to this snapshot. A deployment mismatch is an
  // explicit refresh action rather than mixed pages from different releases.
  useEffect(() => {
    if (!data || version || catalog.isFetching || catalog.isPlaceholderData || catalog.isError) return
    setUrl((previous) => {
      const next = new URLSearchParams(previous)
      next.set('version', data.catalogVersion)
      return next
    }, { replace: true })
  }, [data, version, setUrl, catalog.isFetching, catalog.isPlaceholderData, catalog.isError])

  function update(name: string, value?: string): void {
    setUrl((previous) => {
      const next = new URLSearchParams(previous)
      if (value) next.set(name, value); else next.delete(name)
      if (name !== 'page') next.delete('page')
      return next
    }, { replace: name === 'search' })
  }
  const changed = (catalog.error as { detail?: string } | null)?.detail === 'translation_catalog_changed'
  const busy = catalog.isFetching || debounced !== search.trim()
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <ListPage title="Translations" count={data?.total}
      search={<SearchField type="search" id="translation-search" label="Search translations" placeholder="Search keys, English, Japanese or context" value={search} maxLength={200} onChange={(event) => update('search', event.target.value)} />}
      activeFilterCount={namespace ? 1 : 0}
      filters={<>
        <FilterField active={Boolean(namespace)} label="Namespace" htmlFor="translation-namespace">
          <Select value={namespace ?? 'all'} onValueChange={(value) => update('namespace', value === 'all' ? undefined : value)}>
            <SelectTrigger id="translation-namespace"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All namespaces</SelectItem>
              {[...new Set([...(data?.namespaces ?? []), ...(namespace ? [namespace] : [])])].map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
      </>}
      pagination={<ListPagination page={page} pageCount={pageCount}
        summary={data ? `${total ? Math.min(page * PAGE_SIZE + 1, total) : 0}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}` : 'Loading results…'}
        previousDisabled={busy || page === 0} nextDisabled={busy || !data || page + 1 >= pageCount}
        onPrevious={() => update('page', String(page))} onNext={() => update('page', String(page + 2))} />}
    >
      {catalog.isPending && <p role="status">Loading translations…</p>}
      {catalog.isError && <Notice variant="danger" actionPlacement="below" action={<div className="flex flex-wrap gap-x-2 gap-y-4"><NoticeAction onClick={() => { if (changed) update('version'); else void catalog.refetch() }}>{changed ? 'Reload catalog' : 'Retry'}</NoticeAction>
        {!changed && <NoticeAction onClick={() => setUrl({})}>Clear filters</NoticeAction>}</div>}>
        <p>{changed ? 'The translation catalog changed with a release. Reload to browse the current catalog.' : 'Could not load translations. Retry, or clear invalid filters.'}</p>
      </Notice>}
      {data && data.items.length === 0 && <Notice variant="neutral" action={<NoticeAction onClick={() => { setUrl({}) }}>Browse catalog</NoticeAction>}>
        <p>{total > 0 ? 'This page is outside the results.' : search || namespace ? 'No translations match. Clear the search or filters to browse the catalog.' : 'No translations are packaged in this release.'}</p>
      </Notice>}
      {data && data.items.length > 0 && <div className={TABLE_WRAP} aria-busy={busy}>
        <table className={`${TABLE} table-fixed min-w-[640px]`}>
          <colgroup><col className="w-[45%]" /><col className="w-[27.5%]" /><col className="w-[27.5%]" /></colgroup>
          <thead><tr className={TABLE_HEAD_ROW}><FilterTh className="p-3" label="Key and context" filterDescription={describeColumnFilters(debounced && `Search: “${debounced}”`, namespace && `Namespace: ${namespace}`)} /><FilterTh className="p-3" label="English" filterDescription={debounced ? `Search: “${debounced}”` : undefined} /><FilterTh className="p-3" label="Japanese" filterDescription={debounced ? `Search: “${debounced}”` : undefined} /></tr></thead>
          <tbody>{data.items.map((row) => <tr key={row.messageId} className={TABLE_ROW}>
            <td className="p-3 align-top break-words">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <code className="[overflow-wrap:anywhere]">{row.messageId}</code>
                  <p className="text-text-secondary">{row.enContext}</p>
                </div>
                <Button size="icon" variant="ghost" className="shrink-0"
                  aria-label={`${copied?.key === row.messageId ? 'Copied key' : 'Copy key'} ${row.messageId}`}
                  title={copied?.key === row.messageId ? 'Copied!' : 'Copy key'}
                  onClick={() => {
                    void Promise.resolve().then(() => navigator.clipboard.writeText(row.messageId)).then(() => {
                      setCopied({ key: row.messageId })
                      setCopyStatus(`Copied ${row.messageId}`)
                    }, () => {
                      setCopied(null)
                      setCopyStatus('Could not copy. Select the key and copy it manually.')
                    })
                  }}>
                  <Icon name={copied?.key === row.messageId ? 'done' : 'copy'} />
                </Button>
              </div>
            </td>
            <td className="p-3 align-top whitespace-pre-wrap break-words" lang="en">{row.enValue}</td>
            <td className="p-3 align-top whitespace-pre-wrap break-words" lang="ja">{row.targetValue}</td>
          </tr>)}</tbody>
        </table>
      </div>}
      <p role="status" className={copyStatus.startsWith('Could not') ? 'text-body text-text-secondary' : 'sr-only'}>{copyStatus}</p>
    </ListPage>
  )
}
